import { useState, useMemo, useEffect } from "react";
import { Button, Checkbox, Combobox, Icon, Text, Tag, Spinner, useFilter } from "@ttc3k/trekker";
import { Box, Center, Flex } from "styled-system/jsx";
import { useTranslation } from "react-i18next";
import { Plus, NavArrowLeft, CheckCircleSolid, FloppyDisk } from "iconoir-react";
import {
  Multilingual,
  OperatorManyUseCaseEnum,
  Owner,
  SurveyFragmentFragment,
  useSurveyResponsesForBroadcastResourceQuery,
  useOperatorManyNamesLazyQuery,
} from "gql/generated";
import { getMultilingualString } from "shared/utils";
import { Skeleton } from "shared/components/atoms/Skeleton";
import { BroadcastFormInline } from "./BroadcastFormInline";
import { format } from "date-fns";
import type { UserOperator } from "widgets/booker/contexts/BookerContextProvider/BookerContext";
import { hasPerBusinessQuestions } from "@shared/businessScope";

type MultiSubmissionIndexProps = {
  survey: SurveyFragmentFragment;
  isPreview: boolean;
  broadcastId: string;
  resourceId: string;
  personId?: string;
  personName?: string;
  broadcastName?: Multilingual;
  broadcastOwner?: Owner;
  allowSaveAndEdit?: boolean;
  allowResubmission?: boolean;
  onDirtyChange?: (dirty: boolean) => void;
  teamVisible?: boolean;
  userOperators?: UserOperator[];
  isAdmin?: boolean;
  broadcastOwnerOrgId?: string;
  /** Admin-set label for starting a submission. */
  ctaLabel?: Multilingual | null;
};

export const MultiSubmissionIndex = ({
  survey,
  isPreview,
  broadcastId,
  resourceId,
  personId,
  personName,
  broadcastName,
  broadcastOwner,
  allowSaveAndEdit,
  allowResubmission,
  onDirtyChange,
  teamVisible,
  userOperators,
  isAdmin,
  broadcastOwnerOrgId,
  ctaLabel,
}: MultiSubmissionIndexProps) => {
  const { t } = useTranslation("home", {
    keyPrefix: "VIEWS.BROADCAST",
  });

  const [activeSubmissionIndex, setActiveSubmissionIndex] = useState<
    number | "new" | null
  >(null);

  const [selectedOperatorIds, setSelectedOperatorIds] = useState<string[]>([]);
  const [draftOperatorIds, setDraftOperatorIds] = useState<string[]>([]);

  const perBusinessEnabled = !!survey.perBusinessAnswersEnabled;
  const needsOperatorSelection =
    (teamVisible === true ||
      (perBusinessEnabled && hasPerBusinessQuestions(survey.questions ?? []))) &&
    !isPreview;

  const showOperatorCards =
    needsOperatorSelection &&
    !isAdmin &&
    (userOperators?.length ?? 0) > 1;

  const autoSelectOperator =
    needsOperatorSelection &&
    !isAdmin &&
    userOperators?.length === 1;

  const showOperatorSearch =
    needsOperatorSelection && isAdmin === true;

  useEffect(() => {
    if (autoSelectOperator && userOperators?.[0]) {
      setSelectedOperatorIds([userOperators[0].entityId]);
    }
  }, [autoSelectOperator, userOperators]);

  const [fetchOperators, { data: operatorSearchData, loading: operatorSearchLoading }] =
    useOperatorManyNamesLazyQuery();

  const operatorItems = useMemo(
    () =>
      operatorSearchData?.operatorMany?.map(op => ({
        value: op._entityId as string,
        label: getMultilingualString(op.name),
      })) ?? [],
    [operatorSearchData],
  );

  const { contains } = useFilter({ sensitivity: "base" });
  const {
    collection: operatorCollection,
    filter: filterOperators,
    set: setOperatorItems,
  } = Combobox.useCollection({
    initialItems: operatorItems,
    filter: contains,
  });

  useEffect(() => {
    setOperatorItems(operatorItems);
  }, [operatorItems, setOperatorItems]);

  const [operatorInputValue, setOperatorInputValue] = useState("");

  useEffect(() => {
    if (showOperatorSearch && broadcastOwnerOrgId) {
      fetchOperators({
        variables: {
          useCase: OperatorManyUseCaseEnum.Internal,
          filter: { organizations: [broadcastOwnerOrgId] } as any,
          limit: 10000,
        },
      });
    }
  }, [showOperatorSearch, broadcastOwnerOrgId, fetchOperators]);

  const { data, loading, refetch } = useSurveyResponsesForBroadcastResourceQuery({
    variables: {
      broadcastId,
      resourceId,
      personId: personId ?? "",
    },
    skip: isPreview || !personId,
  });

  const submissions = useMemo(
    () => data?.surveyResponsesForBroadcastResource ?? [],
    [data],
  );

  const handleBack = () => {
    setActiveSubmissionIndex(null);
    if (needsOperatorSelection && !autoSelectOperator) {
      setSelectedOperatorIds([]);
      setDraftOperatorIds([]);
    }
    refetch();
  };

  const handleCreateNew = () => {
    setActiveSubmissionIndex("new");
  };

  const handleOperatorSelected = (opId: string) => {
    setSelectedOperatorIds([opId]);
  };

  const toggleDraftOperator = (opId: string, checked: boolean) =>
    setDraftOperatorIds(prev =>
      checked ? [...prev.filter(id => id !== opId), opId] : prev.filter(id => id !== opId),
    );

  const selectedBusinesses = selectedOperatorIds.map(id => ({
    operatorId: id,
    businessName:
      userOperators?.find(op => op.entityId === id)?.name ??
      operatorItems.find(item => item.value === id)?.label ??
      null,
  }));

  const handleOpenSubmission = (idx: number) => {
    setActiveSubmissionIndex(idx);
  };

  if (loading) {
    return (
      <Center py="600">
        <Spinner w="38px" h="38px" />
      </Center>
    );
  }

  if (
    activeSubmissionIndex === "new" &&
    needsOperatorSelection &&
    selectedOperatorIds.length === 0
  ) {
    return (
      <Flex flexDir="column" gap="300">
        <Flex>
          <Button
            mode="light"
            onClick={() => setActiveSubmissionIndex(null)}
            type="button"
            size="sm"
            Icon={NavArrowLeft}
          >
            {t("FORM_BACK_TO_SUBMISSIONS")}
          </Button>
        </Flex>
        <Text visual="bodySemiBold" color="text.dark">
          {showOperatorCards && perBusinessEnabled
            ? t("FORM_SELECT_BUSINESSES")
            : t("FORM_SELECT_OPERATOR")}
        </Text>
        <Text visual="smallRegular" color="text.mid">
          {showOperatorCards && perBusinessEnabled
            ? t("FORM_SELECT_BUSINESSES_SUBTITLE")
            : t("FORM_SELECT_OPERATOR_SUBTITLE")}
        </Text>

        {showOperatorSearch && (
          <Combobox.Root
            collection={operatorCollection}
            value={[]}
            onValueChange={d => {
              if (d.value[0]) {
                handleOperatorSelected(d.value[0]);
              }
            }}
            inputValue={operatorInputValue}
            onInputValueChange={d => {
              setOperatorInputValue(d.inputValue);
              filterOperators(d.inputValue);
            }}
            onOpenChange={d => {
              if (d.open) setOperatorItems(operatorItems);
            }}
            placeholder={t("FORM_SEARCH_OPERATOR_PLACEHOLDER")}
            openOnClick
            allowCustomValue={false}
          >
            <Combobox.ItemGroup>
              {operatorCollection.items.length === 0 && !operatorSearchLoading && (
                <Combobox.ItemGroupLabel>
                  {t("FORM_NO_OPERATORS_FOUND")}
                </Combobox.ItemGroupLabel>
              )}
              {operatorSearchLoading && operatorCollection.items.length === 0 && (
                <Combobox.ItemGroupLabel>Loading...</Combobox.ItemGroupLabel>
              )}
              {operatorCollection.items.map(item => (
                <Combobox.Item key={item.value} label={item.label} item={item} />
              ))}
            </Combobox.ItemGroup>
          </Combobox.Root>
        )}

        {showOperatorCards && userOperators && !perBusinessEnabled && (
          <Flex flexDir="column" gap="200">
            {userOperators.map(op => (
              <Flex
                key={op.entityId}
                p="300"
                border="1px solid"
                borderColor="border.light"
                borderRadius="200"
                cursor="pointer"
                _hover={{ borderColor: "border.mid" }}
                onClick={() => handleOperatorSelected(op.entityId)}
                alignItems="center"
                gap="200"
              >
                <Text visual="bodySemiBold" color="text.dark">
                  {op.name}
                </Text>
              </Flex>
            ))}
          </Flex>
        )}

        {showOperatorCards && userOperators && perBusinessEnabled && (
          <Flex flexDir="column" gap="200">
            <Flex justifyContent="flex-end">
              <Button
                mode="light"
                size="sm"
                type="button"
                onClick={() =>
                  setDraftOperatorIds(prev => [
                    ...prev,
                    ...userOperators
                      .map(op => op.entityId)
                      .filter(id => !prev.includes(id)),
                  ])
                }
              >
                {t("FORM_SELECT_ALL")}
              </Button>
            </Flex>
            {userOperators.map(op => (
              <Flex
                key={op.entityId}
                p="300"
                border="1px solid"
                borderColor={
                  draftOperatorIds.includes(op.entityId) ? "border.dark" : "border.light"
                }
                borderRadius="200"
                alignItems="center"
                gap="200"
              >
                <Checkbox
                  label={op.name}
                  checked={draftOperatorIds.includes(op.entityId)}
                  onCheckedChange={e =>
                    toggleDraftOperator(op.entityId, e.checked === true)
                  }
                />
              </Flex>
            ))}
            <Flex justifyContent="flex-end">
              <Button
                visual="accent.blue"
                type="button"
                disabled={draftOperatorIds.length === 0}
                onClick={() => setSelectedOperatorIds(draftOperatorIds)}
              >
                {t("FORM_CONTINUE")}
              </Button>
            </Flex>
          </Flex>
        )}
      </Flex>
    );
  }

  if (activeSubmissionIndex !== null) {
    const subIndex =
      activeSubmissionIndex === "new"
        ? undefined
        : activeSubmissionIndex;

    const activeSubmission =
      activeSubmissionIndex !== "new"
        ? submissions.find(s => s?.submissionIndex === activeSubmissionIndex)
        : null;
    const effectivePersonId =
      activeSubmission?.person?._id ?? personId;

    return (
      <Flex flexDir="column" gap="300">
        <Flex>
          <Button
            mode="light"
            onClick={handleBack}
            type="button"
            size="sm"
            Icon={NavArrowLeft}
          >
            {t("FORM_BACK_TO_SUBMISSIONS")}
          </Button>
        </Flex>
        <BroadcastFormInline
          survey={survey}
          isPreview={isPreview}
          broadcastId={broadcastId}
          resourceId={resourceId}
          personId={effectivePersonId}
          savedByPersonId={effectivePersonId !== personId ? personId : undefined}
          personName={personName}
          broadcastName={broadcastName}
          broadcastOwner={broadcastOwner}
          allowSaveAndEdit={allowSaveAndEdit}
          allowResubmission={allowResubmission}
          submissionIndex={subIndex}
          isNewSubmission={activeSubmissionIndex === "new"}
          onDraftSaved={idx => setActiveSubmissionIndex(idx)}
          onDirtyChange={onDirtyChange}
          onSuccess={handleBack}
          userOperators={isAdmin ? undefined : userOperators}
          operatorIds={
            activeSubmissionIndex === "new" && selectedOperatorIds.length > 0
              ? selectedOperatorIds
              : undefined
          }
          businesses={
            activeSubmissionIndex === "new" && selectedBusinesses.length > 0
              ? selectedBusinesses
              : undefined
          }
        />
      </Flex>
    );
  }

  return (
    <Flex flexDir="column" gap="300">
      {/* Create new submission card */}
      <Skeleton
        alignItems="stretch"
        minH="unset"
        cursor="pointer"
        onClick={handleCreateNew}
      >
        <Flex alignItems="center" gap="200" py="100">
          <Icon Element={Plus} color="icon.mid" size="sm" />
          <Text visual="bodySemiBold" color="text.dark">
            {getMultilingualString(ctaLabel ?? {}).trim() ||
              (submissions.length > 0
                ? t("FORM_SUBMIT_ANOTHER")
                : t("FORM_FILL_OUT"))}
          </Text>
        </Flex>
      </Skeleton>

      {/* Existing submission cards */}
      {submissions.length === 0 && (
        <Text visual="smallRegular" color="text.mid" textAlign="center" py="200">
          {t("FORM_NO_SUBMISSIONS_YET")}
        </Text>
      )}
      {submissions.map((sub) => {
        if (!sub) return null;
        const idx = sub.submissionIndex ?? 0;
        const lastHistoryEntry = sub.history?.[sub.history.length - 1];
        const statusLabel =
          sub.status === "SUBMITTED"
            ? t("FORM_SUBMISSION_SUBMITTED")
            : sub.status === "PENDING_REVIEW"
              ? t("FORM_SUBMISSION_PENDING")
              : sub.status === "REJECTED"
                ? t("FORM_SUBMISSION_REJECTED")
                : t("FORM_SUBMISSION_DRAFT");
        const statusVisual =
          sub.status === "SUBMITTED"
            ? "success"
            : sub.status === "PENDING_REVIEW"
              ? "highlight"
              : sub.status === "REJECTED"
                ? ("destructive" as const)
                : ("neutral" as const);
        const lastDate = lastHistoryEntry?.timestamp
          ? format(new Date(lastHistoryEntry.timestamp), "MMM d, yyyy h:mm a")
          : "";

        return (
          <Box
            key={sub._id}
            border="1px solid"
            borderColor="border.light"
            borderRadius="200"
            p="300"
            cursor="pointer"
            _hover={{ bg: "bg.wa.light" }}
            onClick={() => handleOpenSubmission(idx)}
          >
            <Flex justifyContent="space-between" alignItems="center">
              <Flex alignItems="center" gap="150">
                <Icon
                  Element={
                    sub.status === "SUBMITTED"
                      ? CheckCircleSolid
                      : FloppyDisk
                  }
                  color={
                    sub.status === "SUBMITTED"
                      ? "icon.success.mid"
                      : "icon.mid"
                  }
                  size="sm"
                />
                <Flex flexDir="column">
                  <Text visual="bodySemiBold" color="text.dark">
                    {sub.summary ?? t("FORM_SUBMISSION_NUMBER", { number: idx + 1 })}
                  </Text>
                  {lastDate && (
                    <Text visual="smallRegular" color="text.light">
                      {lastDate}
                    </Text>
                  )}
                </Flex>
              </Flex>
              <Flex flexShrink={0} w="90px" ml="150" justifyContent="center">
                <Tag
                  visual={statusVisual}
                  mode="light"
                  textProps={{ textAlign: "center" }}
                >
                  {statusLabel}
                </Tag>
              </Flex>
            </Flex>
          </Box>
        );
      })}
    </Flex>
  );
};
